import { useEffect, useMemo, useState } from 'react';

import {
  useCreateBusinessRuleMutation,
  useGetBusinessRulesQuery,
  useUpdateBusinessRuleMutation,
} from 'features/notification-config/services/businessRules.api';

import type { ProjectDetail } from '../ui/project-detail.types';

export const BANT_BUDGET_THRESHOLDS_RULE_NAME = 'BANT_BUDGET_THRESHOLDS';

// Mirrors lidz_back src/types/International/currencies.ts — must stay in sync.
export const BANT_CURRENCIES = ['CLP', 'UF', 'ARS', 'PEN', 'USD', 'CRC', 'GTQ', 'MXN', 'UYU', 'COP'] as const;
export type BantCurrency = (typeof BANT_CURRENCIES)[number];

export interface BantThresholds {
  currency: BantCurrency;
  minSalary: number;
  minSavings: number;
  salaryEnable: boolean;
  savingsEnable: boolean;
}

export const DEFAULT_BANT_THRESHOLDS: BantThresholds = {
  currency: 'CLP',
  minSalary: 0,
  minSavings: 0,
  salaryEnable: false,
  savingsEnable: false,
};

const isBantCurrency = (value: unknown): value is BantCurrency =>
  typeof value === 'string' && (BANT_CURRENCIES as readonly string[]).includes(value);

const readThresholds = (raw: unknown): BantThresholds => {
  if (!raw || typeof raw !== 'object') return DEFAULT_BANT_THRESHOLDS;
  const value = raw as Partial<Record<keyof BantThresholds, unknown>>;
  return {
    currency: isBantCurrency(value.currency) ? value.currency : DEFAULT_BANT_THRESHOLDS.currency,
    minSalary: typeof value.minSalary === 'number' ? value.minSalary : DEFAULT_BANT_THRESHOLDS.minSalary,
    minSavings: typeof value.minSavings === 'number' ? value.minSavings : DEFAULT_BANT_THRESHOLDS.minSavings,
    salaryEnable: value.salaryEnable === true,
    savingsEnable: value.savingsEnable === true,
  };
};

const sameThresholds = (a: BantThresholds, b: BantThresholds): boolean =>
  a.currency === b.currency &&
  a.minSalary === b.minSalary &&
  a.minSavings === b.minSavings &&
  a.salaryEnable === b.salaryEnable &&
  a.savingsEnable === b.savingsEnable;

export interface BantBusinessRuleState {
  isLoading: boolean;
  isError: boolean;
  ruleExists: boolean;
  isEnabled: boolean;
  isToggling: boolean;
  draft: BantThresholds;
  updateDraft: (patch: Partial<BantThresholds>) => void;
  isDirty: boolean;
  toggle: (nextEnabled: boolean) => Promise<void>;
  save: () => Promise<void>;
  reset: () => void;
}

const useBantBusinessRule = (project: ProjectDetail): BantBusinessRuleState => {
  const isSubproject = project.masterProjectId !== null;
  const { data, isError, isLoading } = useGetBusinessRulesQuery(
    {
      name: BANT_BUDGET_THRESHOLDS_RULE_NAME,
      entityType: 'Project',
      entityId: project.masterProjectId ?? undefined,
      subEntityType: 'Project',
      subEntityId: project.id,
      exact: true,
      limit: 1,
    },
    { skip: !isSubproject }
  );

  const [createRule, { isLoading: isCreating }] = useCreateBusinessRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateBusinessRuleMutation();

  const rule = data?.data[0] ?? null;
  const savedThresholds = useMemo(() => readThresholds(rule?.value), [rule]);

  const [draft, setDraft] = useState<BantThresholds>(savedThresholds);

  useEffect(() => {
    setDraft(savedThresholds);
  }, [savedThresholds]);

  const isEnabled = rule?.enabled === true;
  const isDirty = isEnabled && !sameThresholds(draft, savedThresholds);

  const updateDraft = (patch: Partial<BantThresholds>): void => setDraft((prev) => ({ ...prev, ...patch }));

  const reset = (): void => setDraft(savedThresholds);

  const toggle = async (nextEnabled: boolean): Promise<void> => {
    if (!isSubproject) return;
    if (rule) {
      await updateRule({ id: rule.id, businessRule: { enabled: nextEnabled } }).unwrap();
      return;
    }
    if (!nextEnabled) return;
    await createRule({
      name: BANT_BUDGET_THRESHOLDS_RULE_NAME,
      displayName: `${project.masterProject?.name ?? project.masterProjectId} - ${project.name}`,
      value: DEFAULT_BANT_THRESHOLDS,
      entityType: 'Project',
      entityId: project.masterProjectId,
      subEntityType: 'Project',
      subEntityId: project.id,
      enabled: true,
    }).unwrap();
  };

  const save = async (): Promise<void> => {
    if (!rule || !isDirty) return;
    await updateRule({ id: rule.id, businessRule: { value: draft } }).unwrap();
  };

  return {
    isLoading,
    isError,
    ruleExists: rule !== null,
    isEnabled,
    isToggling: isCreating || isUpdating,
    draft,
    updateDraft,
    isDirty,
    toggle,
    save,
    reset,
  };
};

export default useBantBusinessRule;
